import { KeyboardBinding } from '../types/controls.types';

export const INTERFACE_CONTROLS = {
  UP: {
    displayName: 'Haut',
    description: 'Naviguer vers le haut dans les menus.',
    defaultKeyboard: { code: 'ArrowUp' },
    defaultGamepad: { type: 'button', index: 12, label: 'D-Pad ↑' },
  },
  DOWN: {
    displayName: 'Bas',
    description: 'Naviguer vers le bas dans les menus.',
    defaultKeyboard: { code: 'ArrowDown' },
    defaultGamepad: { type: 'button', index: 13, label: 'D-Pad ↓' },
  },
  LEFT: {
    displayName: 'Gauche',
    description: 'Naviguer vers la gauche dans les menus.',
    defaultKeyboard: { code: 'ArrowLeft' },
    defaultGamepad: { type: 'button', index: 14, label: 'D-Pad ←' },
  },
  RIGHT: {
    displayName: 'Droite',
    description: 'Naviguer vers la droite dans les menus.',
    defaultKeyboard: { code: 'ArrowRight' },
    defaultGamepad: { type: 'button', index: 15, label: 'D-Pad →' },
  },
  CONFIRM: {
    displayName: 'Valider',
    description: 'Confirmer la sélection.',
    defaultKeyboard: { code: 'Enter' },
    defaultGamepad: { type: 'button', index: 1, label: 'A' },
  },
  CANCEL: {
    displayName: 'Retour',
    description: 'Revenir en arrière / annuler.',
    defaultKeyboard: { code: 'Backspace' },
    defaultGamepad: { type: 'button', index: 0, label: 'B' },
  },
  UNPAUSE: {
    displayName: 'Reprendre',
    description: 'Reprendre le jeu.',
    defaultKeyboard: { code: 'Escape' },
    defaultGamepad: { type: 'button', index: 9, label: 'Start' },
  },
  TAB_NEXT: {
    displayName: 'Onglet suivant',
    description: "Passer à l'onglet suivant.",
    defaultKeyboard: { code: 'Tab' },
    defaultGamepad: { type: 'button', index: 5, label: 'RB' },
  },
  TAB_PREV: {
    displayName: 'Onglet précédent',
    description: "Revenir à l'onglet précédent.",
    defaultKeyboard: { code: 'Tab', modifiers: ['ShiftLeft'] },
    defaultGamepad: { type: 'button', index: 4, label: 'LB' },
  },
  SECONDARY: {
    displayName: 'Action secondaire',
    description:
      'Action contextuelle de la card focus (ex. toggle détails, transférer tout un stack…).',
    defaultKeyboard: { code: 'KeyT' },
    defaultGamepad: { type: 'button', index: 2, label: 'Y' },
  },
  TERTIARY: {
    displayName: 'Action tertiaire',
    description:
      'Action contextuelle additionnelle (ex. trier les inventaires).',
    defaultKeyboard: { code: 'KeyV' },
    defaultGamepad: { type: 'button', index: 8, label: 'Select' },
  },
  QUATERNARY: {
    displayName: 'Action quaternaire',
    description:
      "Quatrième action contextuelle (ex. vider l'inventaire dans le coffre).",
    defaultKeyboard: { code: 'KeyR' },
    defaultGamepad: { type: 'button', index: 3, label: 'X' },
  },
  DETAILS: {
    displayName: 'Détails',
    description:
      "Afficher/masquer la fiche détaillée de l'élément focus (toggle).",
    defaultKeyboard: { code: 'KeyH' },
    defaultGamepad: { type: 'button', index: 9, label: 'Start' },
  },
};

export type InterfaceAction = keyof typeof INTERFACE_CONTROLS;

export const DUNGEON_CONTROLS = {
  // --- Movement ---
  MOVE_UP: {
    displayName: 'Déplacement haut',
    description: 'Déplacer le personnage vers le haut.',
    defaultKeyboard: { code: 'KeyW' },
    defaultGamepad: { type: 'button', index: 12, label: 'D-Pad ↑' },
  },
  MOVE_DOWN: {
    displayName: 'Déplacement bas',
    description: 'Déplacer le personnage vers le bas.',
    defaultKeyboard: { code: 'KeyS' },
    defaultGamepad: { type: 'button', index: 13, label: 'D-Pad ↓' },
  },
  MOVE_LEFT: {
    displayName: 'Déplacement gauche',
    description: 'Déplacer le personnage vers la gauche.',
    defaultKeyboard: { code: 'KeyA' },
    defaultGamepad: { type: 'button', index: 14, label: 'D-Pad ←' },
  },
  MOVE_RIGHT: {
    displayName: 'Déplacement droite',
    description: 'Déplacer le personnage vers la droite.',
    defaultKeyboard: { code: 'KeyD' },
    defaultGamepad: { type: 'button', index: 15, label: 'D-Pad →' },
  },

  // --- Combat ---
  BASIC_ATTACK: {
    displayName: 'Attaque de base',
    description: 'Effectuer une attaque de base au corps à corps.',
    defaultKeyboard: { code: 'KeyE' },
    defaultGamepad: { type: 'button', index: 1, label: 'A' },
  },
  INTERACT: {
    displayName: 'Interagir',
    description: "Interagir avec l'élément.",
    defaultKeyboard: { code: 'KeyF' },
    defaultGamepad: { type: 'button', index: 0, label: 'B' },
  },
  FACE: {
    displayName: 'Tourner le personnage',
    description: 'Orienter le personnage sans se déplacer.',
    defaultKeyboard: { code: 'Space' },
    defaultGamepad: { type: 'button', index: 2, label: 'Y' },
  },
  LOCK_DIAGONAL: {
    displayName: 'Verrouillage diagonal',
    description: 'Limiter les déplacements aux diagonales.',
    defaultKeyboard: { code: 'ShiftLeft' },
    defaultGamepad: { type: 'button', index: 5, label: 'RB' },
  },

  // --- Skills (LB + button = 1-4) ---
  SKILL_1: {
    displayName: 'Compétence 1',
    description: 'Activer la compétence 1.',
    defaultKeyboard: { code: 'Digit1' },
    defaultGamepad: {
      type: 'combo',
      modifier: 4,
      modifierLabel: 'LB',
      button: 1,
      buttonLabel: 'A',
    },
  },
  SKILL_2: {
    displayName: 'Compétence 2',
    description: 'Activer la compétence 2.',
    defaultKeyboard: { code: 'Digit2' },
    defaultGamepad: {
      type: 'combo',
      modifier: 4,
      modifierLabel: 'LB',
      button: 0,
      buttonLabel: 'B',
    },
  },
  SKILL_3: {
    displayName: 'Compétence 3',
    description: 'Activer la compétence 3.',
    defaultKeyboard: { code: 'Digit3' },
    defaultGamepad: {
      type: 'combo',
      modifier: 4,
      modifierLabel: 'LB',
      button: 3,
      buttonLabel: 'X',
    },
  },
  SKILL_4: {
    displayName: 'Compétence 4',
    description: 'Activer la compétence 4.',
    defaultKeyboard: { code: 'Digit4' },
    defaultGamepad: {
      type: 'combo',
      modifier: 4,
      modifierLabel: 'LB',
      button: 2,
      buttonLabel: 'Y',
    },
  },
  // --- Dungeon interface ---
  PAUSE: {
    displayName: 'Pause',
    description: 'Mettre le jeu en pause.',
    defaultKeyboard: { code: 'Escape' },
    defaultGamepad: { type: 'button', index: 9, label: 'Start' },
  },
  INVENTORY: {
    displayName: 'Inventaire',
    description: "Ouvrir l'inventaire.",
    defaultKeyboard: { code: 'KeyI' },
    defaultGamepad: { type: 'button', index: 3, label: 'X' },
  },
  OPEN_MAP: {
    displayName: 'Carte',
    description: 'Ouvrir la carte plein écran.',
    defaultKeyboard: { code: 'KeyN' },
    defaultGamepad: { type: 'button', index: 8, label: 'Select' },
  },
};

export type DungeonAction = keyof typeof DUNGEON_CONTROLS;

export const CONTROLS_CONFIG = {
  interface: INTERFACE_CONTROLS,
  dungeon: DUNGEON_CONTROLS,
} as const;

export type GameplayContext = keyof typeof CONTROLS_CONFIG;

export type InputPresetId = 'gamepad' | 'keyboard' | 'keyboard-mouse';

export interface InputPreset {
  id: InputPresetId;
  label: string;
  description: string;
  /** Keyboard binding applied to each dungeon action. */
  dungeonKeyboard: Record<DungeonAction, KeyboardBinding>;
  mouseControl: boolean;
}

const PRESET_GAMEPAD_KEYBOARD: Record<DungeonAction, KeyboardBinding> = {
  MOVE_UP: { code: 'KeyW' },
  MOVE_DOWN: { code: 'KeyS' },
  MOVE_LEFT: { code: 'KeyA' },
  MOVE_RIGHT: { code: 'KeyD' },
  BASIC_ATTACK: { code: 'KeyE' },
  INTERACT: { code: 'KeyF' },
  FACE: { code: 'Space' },
  LOCK_DIAGONAL: { code: 'ShiftLeft' },
  SKILL_1: { code: 'Digit1' },
  SKILL_2: { code: 'Digit2' },
  SKILL_3: { code: 'Digit3' },
  SKILL_4: { code: 'Digit4' },
  PAUSE: { code: 'Escape' },
  INVENTORY: { code: 'KeyI' },
  OPEN_MAP: { code: 'KeyN' },
};

const PRESET_KEYBOARD_ONLY: Record<DungeonAction, KeyboardBinding> = {
  MOVE_UP: { code: 'ArrowUp' },
  MOVE_DOWN: { code: 'ArrowDown' },
  MOVE_LEFT: { code: 'ArrowLeft' },
  MOVE_RIGHT: { code: 'ArrowRight' },
  BASIC_ATTACK: { code: 'Space' },
  INTERACT: { code: 'KeyE' },
  FACE: { code: 'KeyQ' },
  LOCK_DIAGONAL: { code: 'ShiftRight' },
  SKILL_1: { code: 'Digit1' },
  SKILL_2: { code: 'Digit2' },
  SKILL_3: { code: 'Digit3' },
  SKILL_4: { code: 'Digit4' },
  PAUSE: { code: 'Escape' },
  INVENTORY: { code: 'Tab' },
  OPEN_MAP: { code: 'KeyR' },
};

const PRESET_KEYBOARD_MOUSE: Record<DungeonAction, KeyboardBinding> = {
  MOVE_UP: { code: 'KeyW' },
  MOVE_DOWN: { code: 'KeyS' },
  MOVE_LEFT: { code: 'KeyA' },
  MOVE_RIGHT: { code: 'KeyD' },
  BASIC_ATTACK: { code: 'KeyE' },
  INTERACT: { code: 'KeyF' },
  FACE: { code: 'Space' },
  LOCK_DIAGONAL: { code: 'ShiftLeft' },
  SKILL_1: { code: 'Digit1' },
  SKILL_2: { code: 'Digit2' },
  SKILL_3: { code: 'Digit3' },
  SKILL_4: { code: 'Digit4' },
  PAUSE: { code: 'Escape' },
  INVENTORY: { code: 'Tab' },
  OPEN_MAP: { code: 'KeyM' },
};

export const INPUT_PRESETS: Record<InputPresetId, InputPreset> = {
  gamepad: {
    id: 'gamepad',
    label: 'Manette',
    description: 'Manette + clavier classique.',
    dungeonKeyboard: PRESET_GAMEPAD_KEYBOARD,
    mouseControl: false,
  },
  keyboard: {
    id: 'keyboard',
    label: 'Clavier uniquement',
    description: 'Flèches pour se déplacer, actions à gauche.',
    dungeonKeyboard: PRESET_KEYBOARD_ONLY,
    mouseControl: false,
  },
  'keyboard-mouse': {
    id: 'keyboard-mouse',
    label: 'Clavier + souris',
    description: 'WASD pour bouger, visée et clic à la souris.',
    dungeonKeyboard: PRESET_KEYBOARD_MOUSE,
    mouseControl: true,
  },
};

export const KEY_LABELS: Record<string, string> = {
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
  Space: 'Espace',
  Enter: 'Entrée',
  Escape: 'Échap',
  Tab: 'Tab',
  ShiftLeft: 'Maj',
  ShiftRight: 'Maj',
  AltLeft: 'Alt',
  AltRight: 'Alt',
  ControlLeft: 'Ctrl',
  ControlRight: 'Ctrl',
  Backspace: 'Retour arr.',
  Delete: 'Suppr',
  KeyA: 'A',
  KeyB: 'B',
  KeyC: 'C',
  KeyD: 'D',
  KeyE: 'E',
  KeyF: 'F',
  KeyI: 'I',
  KeyM: 'M',
  KeyQ: 'Q',
  KeyR: 'R',
  KeyS: 'S',
  KeyW: 'W',
  KeyX: 'X',
  Digit1: '1',
  Digit2: '2',
  Digit3: '3',
  Digit4: '4',
  Digit5: '5',
  Digit6: '6',
  Digit7: '7',
  Digit8: '8',
};

/** Standard gamepad button labels (Xbox-style layout). */
export const GAMEPAD_BUTTON_LABELS: Record<number, string> = {
  0: 'B',
  1: 'A',
  2: 'Y',
  3: 'X',
  4: 'LB',
  5: 'RB',
  6: 'LT',
  7: 'RT',
  8: 'Select',
  9: 'Start',
  10: 'L3',
  11: 'R3',
  12: 'D-Pad ↑',
  13: 'D-Pad ↓',
  14: 'D-Pad ←',
  15: 'D-Pad →',
  16: 'Home',
};
